/**
 * Pure role-dependent logic for the avatar dropdown (UserMenu), split out so it
 * can be unit-tested in the `node` test env (no DOM / RTL).
 *
 * Hierarchy (locked 2026-05-24):
 *   Agent:            Profile · Availability · Days off · ─── · Sign out
 *   Manager / super:  Profile · Days off · ─── · Sign out
 *   Client:           Profile · ─── · Sign out
 *
 * "Availability" (the recurring weekly schedule for client-appointment slots)
 * is agent-only — managers/super_admins don't take client appointments. Their
 * TEAM time-off oversight lives on /manager/team (GitLab #66), reachable from
 * the main nav, so it is intentionally NOT duplicated here (supersedes the
 * stale "manager Availability → mode=manager" ask in #62).
 *
 * "Days off" is each staffer's OWN one-off absences (mode="self").
 *
 * When the account is flagged for forced credential rotation the leading item
 * is "Set password" (routes to /auth/change-password) instead of Profile.
 */
export type UserMenuItemKey =
  | 'profile'
  | 'set_password'
  | 'availability'
  | 'days_off'
  | 'sign_out';

export type UserMenuTone = 'normal' | 'warning' | 'danger';

export interface UserMenuItem {
  key: UserMenuItemKey;
  label: string;
  tone: UserMenuTone;
  /** True when a divider should be drawn ABOVE this item. */
  dividerBefore?: boolean;
}

/** The screen each item opens; "Days off" opens a window in place and "Sign out" leaves. UserMenu
 *  navigates here, and the menu-coverage test counts these as the role's ways into a screen. */
export const USER_MENU_PATHS: Readonly<Partial<Record<UserMenuItemKey, string>>> = {
  profile: '/profile',
  set_password: '/auth/change-password',
  availability: '/agent/availability',
};

// Who may record their OWN absences. The API refuses a manager here ("Only agents can
// manage their own time-off"): a manager records an agent's days off from the team
// screen, and has no absences of their own to record.
const OWN_DAYS_OFF_ROLES = new Set(['agent', 'super_admin']);

/** The ordered menu items for a given role + forced-rotation flag. */
export function userMenuItems(role: string, mustChangePassword: boolean): UserMenuItem[] {
  const items: UserMenuItem[] = [];

  items.push(
    mustChangePassword
      ? { key: 'set_password', label: 'Set password', tone: 'warning' }
      : { key: 'profile', label: 'Profile', tone: 'normal' },
  );

  // Availability = weekly appointment schedule → agents only.
  if (role === 'agent') {
    items.push({ key: 'availability', label: 'Availability', tone: 'normal' });
  }

  // Days off = own absences → the roles the API lets record them.
  if (OWN_DAYS_OFF_ROLES.has(role)) {
    items.push({ key: 'days_off', label: 'Days off', tone: 'normal' });
  }

  items.push({ key: 'sign_out', label: 'Sign out', tone: 'danger', dividerBefore: true });
  return items;
}
